
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CrmConfig } from "@/types/database";

const formSchema = z.object({
  webhookUrl: z.string().url("Please enter a valid webhook URL"),
  isEnabled: z.boolean()
});

type FormData = z.infer<typeof formSchema>;

interface CrmWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crmType: string;
  crmName: string;
  userId: string;
}

export const CrmWebhookDialog = ({
  open,
  onOpenChange,
  crmType,
  crmName,
  userId
}: CrmWebhookDialogProps) => {
  const [isTesting, setIsTesting] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: configData
  } = useQuery({
    queryKey: ['crm-config', userId, crmType],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('crm_configs').select('*')
        .eq('user_id', userId as any)
        .eq('crm_type', crmType as any)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data as CrmConfig | null;
    },
    enabled: !!userId && open // Only fetch when dialog is open
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      webhookUrl: '',
      isEnabled: false
    }
  });

  // Update form values when config data is loaded
  useEffect(() => {
    if (configData) {
      form.reset({
        webhookUrl: configData.webhook_url || '',
        isEnabled: configData.is_enabled || false
      });
    }
  }, [configData, form]);

  const testConnection = async (webhookUrl: string) => {
    if (!webhookUrl) {
      toast.error("Please enter a webhook URL first");
      return;
    }
    
    setIsTesting(true);
    try {
      // Use our edge function to proxy the request
      const { error } = await supabase.functions.invoke('send-crm-test', {
        body: {
          test_mode: true,
          webhook_url: webhookUrl,
          crm_type: crmType
        }
      });
      
      if (error) throw error;
      toast.success(`Test message sent to ${crmName} successfully!`);
    } catch (error) {
      console.error('Test connection error:', error);
      toast.error(`Failed to send test to ${crmName}. Please check your webhook URL.`);
    } finally {
      setIsTesting(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // First check if a configuration already exists for this user and CRM type
      const { data: existingConfig } = await supabase
        .from('crm_configs')
        .select('id')
        .eq('user_id', userId as any)
        .eq('crm_type', crmType as any)
        .maybeSingle();
      
      let result;
      
      if (existingConfig?.id) {
        // Update existing config
        result = await supabase
          .from('crm_configs')
          .update({
            webhook_url: data.webhookUrl,
            is_enabled: data.isEnabled
          } as any)
          .eq('id', existingConfig.id);
      } else {
        // Insert new config
        result = await supabase
          .from('crm_configs')
          .insert({
            user_id: userId,
            crm_type: crmType,
            webhook_url: data.webhookUrl,
            is_enabled: data.isEnabled
          } as any);
      }
      
      if (result.error) throw result.error;
      
      toast.success(`${crmName} webhook configuration saved successfully!`);
      queryClient.invalidateQueries({
        queryKey: ['crm-config', userId, crmType]
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Failed to save ${crmName} webhook configuration`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect {crmName}</DialogTitle>
          <DialogDescription>
            Set up webhook integration to send leads from this platform to your {crmName} account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h3 className="font-medium text-base">Enable {crmName} Integration</h3>
                  <p className="text-sm text-muted-foreground">Test connection to enable</p>
                </div>
                <FormField
                  control={form.control}
                  name="isEnabled"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="webhookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Webhook URL</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={`Enter your ${crmName} webhook URL...`} 
                      {...field} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">How to get your webhook URL:</h4>
              <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1">
                {getCrmInstructions(crmType)}
              </ol>
            </div>

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => testConnection(form.getValues().webhookUrl)}
                disabled={isTesting || !form.getValues().webhookUrl}
              >
                {isTesting ? "Testing..." : "Test Connection"}
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90">Save Configuration</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

function getCrmInstructions(crmType: string): JSX.Element[] {
  const instructions: Record<string, JSX.Element[]> = {
    salesforce: [
      <li key="sf1">Go to Setup → Platform Tools → Process Automation → Workflow Rules</li>,
      <li key="sf2">Create a new Workflow Rule that includes an "Outbound Message" action</li>,
      <li key="sf3">Configure which object (Leads) will receive data and save the webhook URL</li>
    ],
    hubspot: [
      <li key="hs1">Navigate to Settings → Integrations → API key</li>,
      <li key="hs2">Go to "Webhooks" tab and click "Create webhook"</li>,
      <li key="hs3">Configure which events will trigger the webhook and copy the URL</li>
    ],
    zoho: [
      <li key="zo1">Go to Setup → Developer Space → Webhooks</li>,
      <li key="zo2">Create a new webhook for the Leads module</li>,
      <li key="zo3">Copy the generated webhook URL</li>
    ],
    pipedrive: [
      <li key="pd1">Navigate to Settings → Company Settings → API</li>,
      <li key="pd2">Go to Webhooks tab and click "Add Webhook"</li>,
      <li key="pd3">Select which events to listen for and copy the URL</li>
    ],
    monday: [
      <li key="mo1">Go to your Monday board's settings</li>,
      <li key="mo2">Navigate to "Integrations" → "API"</li>,
      <li key="mo3">Create a new webhook and copy the URL</li>
    ]
  };

  return instructions[crmType] || [<li key="default">Check your CRM's documentation for webhook setup instructions</li>];
}
